// Auto-refresh coordinator for left-open tabs.
//
// Design: docs/letter-flick-refresh-and-reload.html (the investment-based
// two-gate model below is binding; supersedes the blunt single safe predicate
// from the 2026-05-31 design spec).
//
// Owns BOTH tiers on one trigger surface (visibilitychange + focus + pageshow +
// a re-armed midnight setTimeout + a short clock tick + a Tier-2 backstop):
//   - Tier 1 (rollover-in-place): when the local calendar day changes, swap to
//     the new day's puzzle WITHOUT a page reload by re-entering the canonical
//     mountDaily() path.
//   - Tier 2 (new-release pickup): poll a static /version.json; when a newer
//     build is deployed, reload immediately if safe, else DEFER the reload
//     (silently — no banner) and fire it at the next safe moment, mirroring the
//     Tier-1 deferred-rollover model.
//
// THE TWO GATES — opposite temperaments, both keyed on INVESTMENT in this board
// (has a flick or undo landed on the current puzzle), never on status==='playing'
// or "a modal is open":
//   - canRollOver (new day) — AGGRESSIVE. Modals NEVER block it: the board swaps
//     behind a passive modal, and the stale game-over modal is dismissed by
//     mountDaily()'s existing closeEndgame(). It defers only for an in-flight
//     animation/gesture (momentary), an in-progress solve (let them finish), or
//     a fresh result modal still on screen (don't yank it — releases the instant
//     they close it, so the modal-close re-check rolls over). An untouched board
//     rolls over immediately; a finished result whose modal is closed rolls over;
//     a board left idle past COMMIT_GRACE_MS (abandoned solve, or a stale result
//     left open while away) also rolls over.
//   - canReload (new build) — GENTLE. No day-urgency, so it stays
//     transition-timed: it still defers while a modal is open (a reload would
//     nuke it) and while a game is actively committed. The release from "a modal
//     blocks reload forever" is: a new-day rollover dismisses the endgame modal,
//     and modal-close (recheck) fires the deferred reload.
//
// This module has NO DOM coupling beyond the host callbacks it is handed and
// NO imports from controller/persistence — it talks to the app only through
// the RefreshHost accessor object. That keeps the decision logic pure and
// unit-testable with plain fakes. The one app import is `formatLocalDate` from
// daily.ts (a pure date formatter) so date-change detection uses the exact
// same formatter the persistence layer writes with.

import { formatLocalDate } from './daily'
import { fetchServedBuildId } from './versionCheck'

export type GameStatusLike = 'idle' | 'playing' | 'won' | 'lost'

// Live snapshot the gate predicates read. Extracted as a pure record so the
// crux logic lives outside the untestable main() closure and stays unit-testable
// with plain fixtures. `msSinceLastActivity` is null when no flick/undo has
// landed on this mount yet (a fresh, untouched board).
export interface DisruptInputs {
  readonly mode: 'daily' | 'practice' | 'dev'
  readonly status: GameStatusLike
  readonly isAnimating: boolean
  readonly isGestureActive: boolean
  readonly hasCommittedMove: boolean
  readonly msSinceLastActivity: number | null
  readonly isModalOpen: boolean
}

// How long we protect a committed in-progress game from a new-day rollover.
// Past this window (no flick/undo for ~10 min, or the player left and returned
// on the new day), the board is treated as abandoned and rolls over to today —
// a stale prior-day board cannot win today's result anyway.
export const COMMIT_GRACE_MS = 10 * 60_000

// Invested in this board AND active within the grace window: at least one
// flick/undo has landed and the last one was recent. An untouched board (no
// committed move), a restored board (null timestamp), and a board left idle
// past the grace window all read false.
function recentlyActive(
  i: Pick<DisruptInputs, 'hasCommittedMove' | 'msSinceLastActivity'>,
): boolean {
  return (
    i.hasCommittedMove &&
    i.msSinceLastActivity !== null &&
    i.msSinceLastActivity < COMMIT_GRACE_MS
  )
}

// The player is mid-solve: still playing, recently active. Defer a rollover
// (let them finish) regardless of any modal. Released when they finish (status
// leaves 'playing') or go idle past the grace window (abandoned).
export function isSolvingNow(
  i: Pick<DisruptInputs, 'status' | 'hasCommittedMove' | 'msSinceLastActivity'>,
): boolean {
  return i.status === 'playing' && recentlyActive(i)
}

// A fresh result is on screen: the game JUST finished and its result modal is
// open. Don't yank it — but this releases the instant the modal closes (so the
// modal-close re-check rolls over) or once it goes stale (past the grace window,
// e.g. a result left open while the player was away). The finishing flick is the
// last activity, so "recent" tracks how long ago they finished.
export function isFreshResultOpen(
  i: Pick<DisruptInputs, 'status' | 'isModalOpen' | 'hasCommittedMove' | 'msSinceLastActivity'>,
): boolean {
  return (i.status === 'won' || i.status === 'lost') && i.isModalOpen && recentlyActive(i)
}

// Rollover gate (new day) — aggressive. Modals do NOT block it; defers only for
// an in-flight animation/gesture, an in-progress solve, or a fresh result modal
// still on screen. An untouched board, a finished result whose modal is closed,
// and a stale board all roll over.
export function canRollOver(i: DisruptInputs): boolean {
  return (
    i.mode === 'daily' &&
    !i.isAnimating &&
    !i.isGestureActive &&
    !isSolvingNow(i) &&
    !isFreshResultOpen(i)
  )
}

// Reload gate (new build) — gentle. Same as rollover but a modal HARD-blocks it
// (a reload would nuke any open modal). Released when the modal closes (the
// close re-check fires the deferred reload) and the board is no longer mid-solve.
export function canReload(i: DisruptInputs): boolean {
  return (
    i.mode === 'daily' &&
    !i.isAnimating &&
    !i.isGestureActive &&
    !i.isModalOpen &&
    !isSolvingNow(i)
  )
}

export interface RefreshHost {
  // Tier 1 action — re-enter the canonical rollover path (mountDaily()).
  readonly mountDaily: () => void
  // Rollover gate (defined in main(), reads live closure state via canRollOver).
  // Aggressive — modals do not block it.
  readonly canRollOverNow: () => boolean
  // Reload gate (defined in main(), reads live closure state via canReload).
  // Gentle — defers while a modal is open.
  readonly canReloadNow: () => boolean
  // Tier-1 a11y announcement (polite). Surfaced as a self-dismissing hint
  // toast; still called AFTER mountDaily() returns so the toast describes the
  // already-mounted puzzle (design §13 #10).
  readonly announceRollover: (localDate: string) => void
  // Tier-2 action — reload the page to pick up a newer deployed build.
  readonly autoReload: () => void
  // Test seam: inject "now". Defaults to () => new Date().
  readonly now?: () => Date
}

export interface RefreshCoordinator {
  start(): void
  dispose(): void
  checkDateRollover(): void
  checkVersion(opts?: { force?: boolean }): void
  onGameEnded(): void
  recheck(): void
}

export interface RefreshOptions {
  readonly enableVersionCheck?: boolean
  readonly versionBackstopMs?: number | null
  readonly minVersionCheckMs?: number
}

const DEFAULT_MIN_VERSION_CHECK_MS = 60_000
// Foreground backstop: shortened 30 → 5 min so a foreground-idle tab notices a
// deploy within ~5 min instead of up to 30. One tiny GET / 5 min, still gated by
// the 60s throttle.
const DEFAULT_VERSION_BACKSTOP_MS = 5 * 60_000
// Tier-1 self-heal tick: a foreground tab re-checks the date every 30s so a
// missed one-shot midnight timer still rolls over. A backgrounded tab freezes
// this too, so it complements (does not replace) the pageshow resume trigger.
const TICK_MS = 30_000
// Floor for the midnight re-arm so a degenerate computed delay can't busy-loop
// the timer (design §13 #9).
const MIN_REARM_MS = 1_000
// Small skew margin past local midnight so the timer fires AFTER the date has
// actually rolled, not a few ms early (design §4.1).
const MIDNIGHT_SKEW_MS = 2_000

// ms until the next LOCAL midnight from `now`. Built via the (y, m, d+1)
// constructor — the same DST-correct local-midnight math endOfPuzzleDay
// uses — so the timer fires at the right wall-clock instant across DST shifts.
function msUntilNextLocalMidnight(now: Date): number {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  return tomorrow.getTime() - now.getTime()
}

export function createRefreshCoordinator(
  host: RefreshHost,
  loadedBuildId: string,
  options?: RefreshOptions,
): RefreshCoordinator {
  const now = host.now ?? ((): Date => new Date())
  const enableVersionCheck = options?.enableVersionCheck ?? true
  const versionBackstopMs = options?.versionBackstopMs ?? DEFAULT_VERSION_BACKSTOP_MS
  const minVersionCheckMs = options?.minVersionCheckMs ?? DEFAULT_MIN_VERSION_CHECK_MS

  // Single guard against duplicate rollovers from near-simultaneous focus +
  // visibilitychange. Seeded at start() (NOT construction) so no auto-rollover
  // can fire before the initial mount/onboarding fork resolves (design §13 #6).
  let lastKnownLocalDate: string | null = null
  // NOTE: there is intentionally no separate `pendingRollover` flag. A deferred
  // rollover is carried purely by NOT advancing `lastKnownLocalDate` when a
  // date change is detected-but-unsafe (see checkDateRollover). Because the
  // guard still reads yesterday, the very next safe re-check —
  // onGameEnded()/recheck() or any focus/visibility/midnight trigger — re-runs
  // checkDateRollover(), still sees today !== lastKnownLocalDate, and applies
  // the rollover. That un-advanced guard IS the pending state (design §4.2).

  let lastVersionCheckMs = 0
  // Set true once a NEWER build is detected but a reload couldn't fire safely
  // (mid-game, modal open, animating, gesture active). Once set, we KNOW a
  // newer build exists — no need to re-fetch — so the next safe moment reloads.
  // This is the Tier-2 analogue of the Tier-1 un-advanced date guard: it IS the
  // deferred-reload state. It never survives a reload (a fresh page starts at
  // false), so there's no carryover risk.
  let pendingReload = false
  let versionCheckInFlight = false

  let midnightTimer: ReturnType<typeof setTimeout> | null = null
  let backstopTimer: ReturnType<typeof setInterval> | null = null
  let tickTimer: ReturnType<typeof setInterval> | null = null
  let started = false

  function performRollover(today: string): void {
    host.mountDaily()
    // Advancing the guard here is what clears any deferred-rollover state: the
    // next checkDateRollover() now sees today === lastKnownLocalDate and no-ops.
    lastKnownLocalDate = today
    // AFTER the re-mount returns so the announcement describes the puzzle
    // that's actually on screen (design §13 #10).
    host.announceRollover(today)
  }

  // Tier-2 deferred-reload applier. Mirrors how a deferred rollover is applied
  // by checkDateRollover: once we know a newer build exists (pendingReload), the
  // next safe moment reloads. Called from every safe-moment hook the deferred
  // rollover uses (onGameEnded / recheck / the focus/visibility/midnight
  // triggers), so a reload that couldn't fire mid-game lands the instant the
  // game returns to a safe state — with no banner, ever.
  function maybeReloadForPending(): void {
    if (pendingReload && host.canReloadNow()) {
      host.autoReload()
    }
  }

  function checkDateRollover(): void {
    // Not started yet (lastKnownLocalDate unseeded) → nothing to compare.
    if (lastKnownLocalDate === null) return
    const today = formatLocalDate(now())
    if (today === lastKnownLocalDate) {
      // No date change. If a rollover was pending from a prior detection it has
      // since been applied (lastKnownLocalDate would advance) — nothing to do.
      return
    }
    // A day change is detected.
    if (host.canRollOverNow()) {
      performRollover(today)
    } else {
      // Defer. Do NOT advance lastKnownLocalDate — leaving the guard on
      // yesterday is exactly what makes the next safe re-check (onGameEnded /
      // recheck / the next focus/visibility/midnight trigger) re-detect the
      // change and apply the rollover. No separate pending flag is needed.
    }
  }

  // `opts.force` bypasses the 60s throttle (still respecting an in-flight
  // fetch) — used by event-driven probes that must NOT be silently swallowed by
  // the window: the pageshow resume trigger and the endgame-close recheck(), so
  // returning to the tab / finishing a puzzle always checks for a newer build.
  // The cadence triggers (focus / visibility / backstop) call it un-forced and
  // keep the throttle.
  function checkVersion(opts?: { force?: boolean }): void {
    if (!enableVersionCheck) return
    if (versionCheckInFlight) return
    if (opts?.force !== true) {
      const t = now().getTime()
      if (t - lastVersionCheckMs < minVersionCheckMs) return
    }
    versionCheckInFlight = true
    void fetchServedBuildId(loadedBuildId)
      .then((result): void => {
        // Throttle gate is set ONLY on a completed fetch (any outcome), so a
        // transient offline 'unknown' doesn't suppress the next real check for
        // the whole window (design §13 #8). fetchServedBuildId never rejects,
        // but a thrown path would skip this and leave the gate open.
        lastVersionCheckMs = now().getTime()
        if (result.outcome !== 'newer') return
        // A newer build is deployed. Reload now if safe; otherwise mark the
        // reload pending and let the next safe-moment hook (maybeReloadForPending)
        // fire it. No banner — the deferral is silent.
        if (host.canReloadNow()) {
          host.autoReload()
        } else {
          pendingReload = true
        }
      })
      .finally((): void => {
        versionCheckInFlight = false
      })
  }

  // Called from main()'s handleGameEnd (as its LAST statement, design §13 #1).
  // Detects a date change that happened silently DURING play (player started at
  // 23:58, finished at 00:01 with no focus event). The endgame modal is open at
  // this point, so checkDateRollover() defers; recheck() applies it once the
  // modal closes.
  function onGameEnded(): void {
    checkDateRollover()
    // Also apply a deferred Tier-2 reload: a newer build detected mid-game can
    // now fire (the modal is still open here, so this typically defers to
    // recheck(), but the call is harmless and symmetric with rollover).
    maybeReloadForPending()
  }

  // Called from main()'s handleEndgameClose. Applies a pending rollover — and a
  // pending Tier-2 reload — once the results modal is dismissed. The FORCED
  // version probe (bypassing the 60s throttle) makes "finish a puzzle, close the
  // Solved / Out-of-Undos popover" always check for a newer build — the modal is
  // closed by now, so a newer build reloads immediately.
  function recheck(): void {
    checkDateRollover()
    maybeReloadForPending()
    checkVersion({ force: true })
  }

  function onVisibilityChange(): void {
    if (document.visibilityState !== 'visible') return
    checkDateRollover()
    maybeReloadForPending()
    checkVersion()
  }

  function onFocus(): void {
    checkDateRollover()
    maybeReloadForPending()
    checkVersion()
  }

  // Restore from the back/forward cache (bfcache). On iOS, returning to a
  // long-backgrounded tab can fire ONLY pageshow — not visibilitychange or
  // focus — so without this listener a tab backgrounded across midnight never
  // re-checks the date on return (the reported "stale puzzle after midnight"
  // bug). Forces the version probe too, since a genuine resume should always
  // re-check for a deploy regardless of the throttle window.
  function onPageShow(): void {
    checkDateRollover()
    maybeReloadForPending()
    checkVersion({ force: true })
  }

  function armMidnightTimer(): void {
    if (midnightTimer !== null) {
      clearTimeout(midnightTimer)
      midnightTimer = null
    }
    const delay = Math.max(msUntilNextLocalMidnight(now()) + MIDNIGHT_SKEW_MS, MIN_REARM_MS)
    midnightTimer = setTimeout((): void => {
      // Re-derive the date INSIDE the handler so a late/throttled fire still
      // detects the correct current date (design §13 #9).
      checkDateRollover()
      // A still-pending Tier-2 reload can also become safe at this instant
      // (e.g. a tab that idled past midnight with the game in a terminal state).
      maybeReloadForPending()
      // Always re-arm — whether we rolled over or deferred — so a still-pending
      // rollover keeps its timer for the following midnight.
      armMidnightTimer()
    }, delay)
  }

  function start(): void {
    if (started) return
    started = true
    // Seed the guard at start() so the FIRST trigger after the initial mount
    // compares against today (no auto-rollover before the fork resolves).
    lastKnownLocalDate = formatLocalDate(now())

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onFocus)
    window.addEventListener('pageshow', onPageShow)
    armMidnightTimer()

    // Tier-1 self-heal: re-check the date on a short interval so a foreground
    // tab still rolls over even if the one-shot midnight timer was missed or
    // throttled. A backgrounded tab freezes this (and the midnight timer), which
    // is why the pageshow resume trigger above is the real fix for that case.
    tickTimer = setInterval((): void => {
      checkDateRollover()
      maybeReloadForPending()
    }, TICK_MS)

    if (enableVersionCheck && versionBackstopMs !== null) {
      // A deploy can land while a tab is foreground-idle; the focus/visibility
      // triggers miss that. A throttled backstop catches it (design §5.4).
      backstopTimer = setInterval((): void => {
        checkVersion()
      }, versionBackstopMs)
    }
  }

  function dispose(): void {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('focus', onFocus)
    window.removeEventListener('pageshow', onPageShow)
    if (midnightTimer !== null) {
      clearTimeout(midnightTimer)
      midnightTimer = null
    }
    if (backstopTimer !== null) {
      clearInterval(backstopTimer)
      backstopTimer = null
    }
    if (tickTimer !== null) {
      clearInterval(tickTimer)
      tickTimer = null
    }
    started = false
  }

  return {
    start,
    dispose,
    checkDateRollover,
    checkVersion,
    onGameEnded,
    recheck,
  }
}
